import { useCallback, useEffect, useRef, useState } from "react";
import { useBreakpoints } from "@otrium/core";
import { useDebouncedCallback } from "use-debounce";
import { ArrowBack } from "src/icons/ArrowBack";
import { ArrowNext } from "src/icons/ArrowNext";
import {
  CarouselArrowButton,
  CarouselContainer,
  CarouselTrack,
} from "./Carousel.styled";
import { isEqualArray } from "src/utils/equal-array";
import { theme } from "src/theme";
import type { CarouselProps } from "./types";
import { resolveContainerPadding, resolveItemsPerView } from "./utils";
import classNames from "classnames";

function Carousel({
  items,
  itemsPerView = { mobile: 1.75, tablet: 6, desktop: 6 },
  gap = theme.space.space24,
  trackCarouselViewed,
  onNavigate,
  withNavigation,
  parentPadding,
  fullWidth,
  fullWidthMobile,
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const { isTablet, isDesktop } = useBreakpoints();
  const resolvedItemsPerView = resolveItemsPerView(itemsPerView);
  const resiolvedParentPaddings = parentPadding
    ? resolveContainerPadding(parentPadding)
    : undefined;

  const updateScrollButtons = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    setCanScrollPrev(track.scrollLeft > 0);
    setCanScrollNext(
      track.scrollLeft < track.scrollWidth - track.clientWidth - 1
    );
  }, []);

  const onPrevClick = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    track.scrollBy({ left: -track.clientWidth, behavior: "smooth" });
    onNavigate?.("prev");
  }, [onNavigate]);

  const onNextClick = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    track.scrollBy({ left: track.clientWidth, behavior: "smooth" });
    onNavigate?.("next");
  }, [onNavigate]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollButtons();

    track.addEventListener("scroll", updateScrollButtons, { passive: true });
    return () => track.removeEventListener("scroll", updateScrollButtons);
  }, [updateScrollButtons]);

  // Re-evaluate scroll button state when items or breakpoint changes
  useEffect(() => {
    updateScrollButtons();
  }, [items.length, isDesktop, isTablet, updateScrollButtons]);

  const visibleIndexesRef = useRef<number[]>([]);

  const fireTracking = useDebouncedCallback(() => {
    if (visibleIndexesRef.current.length && trackCarouselViewed) {
      trackCarouselViewed(visibleIndexesRef.current);
    }
  }, 500);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !trackCarouselViewed) return;

    const visibleSet = new Set<number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number(entry.target.getAttribute("data-index"));

          if (entry.isIntersecting) {
            visibleSet.add(index);
          } else {
            visibleSet.delete(index);
          }
        }

        const sorted = [...visibleSet].sort((a, b) => a - b);

        if (!isEqualArray(sorted, visibleIndexesRef.current)) {
          visibleIndexesRef.current = sorted;
          fireTracking();
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const child of track.children) {
      observer.observe(child);
    }

    return () => observer.disconnect();
  }, [items.length, trackCarouselViewed, fireTracking]);

  return (
    <CarouselContainer
      className={classNames({
        ["full-width"]: fullWidth,
        ["full-width-mobile"]: fullWidthMobile,
      })}
      style={{ ...resiolvedParentPaddings } as React.CSSProperties}
    >
      {withNavigation ? (
        <CarouselArrowButton
          onClick={onPrevClick}
          className={classNames({ hidden: !canScrollPrev })}
          aria-label="Previous"
          data-testid="css-carousel-prev"
        >
          <ArrowBack color="#999" role="presentation" />
        </CarouselArrowButton>
      ) : null}

      <CarouselTrack
        ref={trackRef}
        style={{
          ...resolvedItemsPerView,
          ...resiolvedParentPaddings,
          ...({ "--gap": gap } as React.CSSProperties),
        }}
        role="list"
        aria-label="carousel"
        className={classNames({
          ["full-width"]: fullWidth,
          ["full-width-mobile"]: fullWidthMobile,
        })}
      >
        {items.map((item, index) => (
          <div
            key={index}
            data-index={index}
            className="carousel-slide"
            role="listitem"
            aria-label={`item ${index + 1} of ${items.length}`}
          >
            {item}
          </div>
        ))}
      </CarouselTrack>

      {withNavigation ? (
        <CarouselArrowButton
          onClick={onNextClick}
          className={classNames({ hidden: !canScrollNext })}
          aria-label="Next"
          data-testid="css-carousel-next"
        >
          <ArrowNext color="#999" role="presentation" />
        </CarouselArrowButton>
      ) : null}
    </CarouselContainer>
  );
}

export { Carousel };
